/**
 * The one escape hatch pallet grants, and it is deliberately a narrow one:
 * `data-*` attributes, spread onto a component's root element.
 *
 * **Why it exists.** A test harness has to be able to name a thing. Once a
 * screen's overlays and chips are pallet's rather than the call site's, the
 * call site has no element of its own left to hang `data-test-role` on, and
 * the e2e suite stops being able to *locate* them — not as a failed
 * assertion, as a failed query (SR-301 phase 6 §5b; seven `scope-chip`
 * assertions and one modal locator).
 *
 * **Why it is bounded to `data-*` and must stay that way.** The whole
 * direction of SR-301 is that pallet stops being reachable-past: phase 4's D4
 * removed `className` from `Button` outright, on the rule that a call site
 * needing one has found a missing variant, not a missing hatch. A `data-*`
 * attribute cannot restyle anything or change a component's box model — it
 * only labels. So this is an identity affordance, **not** a `className` /
 * `style` hatch, and widening it into one would undo D4 by the back door.
 *
 * Note that `dataAttributes` filters at *runtime* as well as typing at
 * compile time: the type alone would not stop a `{...someProps}` spread from
 * carrying a `className` through.
 */
export type DataAttributes = {
  readonly [name: `data-${string}`]: string | number | boolean | undefined;
};

/**
 * The `data-*` entries of `props`, and only those.
 *
 * Returns a fresh object safe to spread onto a DOM element. Keys that are not
 * `data-`-prefixed are dropped silently rather than thrown on: the type
 * already rejects them at every call site pallet controls, and this is the
 * belt to that's braces.
 */
export function dataAttributes(props: object): DataAttributes {
  const entries = Object.entries(props).filter(
    ([name, value]) => name.startsWith("data-") && value !== undefined,
  );

  return Object.fromEntries(entries) as DataAttributes;
}
